/**
 * What build am I looking at? One string: `v26.09.18`, the date the code was
 * packaged.
 *
 * Deliberately NOT part of envConfig. Everything in there is runtime config,
 * rewritten into /config.js each time the container starts — which is exactly
 * the wrong source for this. One image is built per merge and then promoted:
 * staging today, production days later, with restarts in between. A stamp read
 * at container start would report the restart; a stamp read at image build time
 * would report a rebuild. Both would be the date nobody asked about.
 *
 * So the date comes from the COMMIT, substituted into the bundle at build time
 * by vite.config.ts. Build the same commit on any machine on any day and the
 * version is identical — packaged on the 19th, still `v26.09.19` when it
 * reaches production on the 23rd.
 */

/** ISO 8601 committer date of the packaged commit. */
export const APP_COMMIT_DATE: string =
    typeof __APP_COMMIT_DATE__ === 'string' ? __APP_COMMIT_DATE__ : ''

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * `v26.09.18` — year, month, day of the packaged commit. Empty when the build
 * carried no date at all (nothing is better than a wrong version).
 */
export const appVersionLabel = (isoDate: string = APP_COMMIT_DATE): string => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime())) return ''
    // Read back in the commit's own offset, not the viewer's: a late-evening
    // commit on the 19th must not read as the 20th for anyone further east.
    // A trailing `Z`, or no offset at all, leaves this at 0 — plain UTC.
    const offset = /([+-])(\d{2}):?(\d{2})$/.exec(isoDate)
    const offsetMinutes = offset
        ? (offset[1] === '-' ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3]))
        : 0
    const shifted = new Date(date.getTime() + offsetMinutes * 60_000)
    const year = pad(shifted.getUTCFullYear() % 100)
    return `v${year}.${pad(shifted.getUTCMonth() + 1)}.${pad(shifted.getUTCDate())}`
}
